/*******************************************************************************
 * Copyright (c) 2024-2026 Malond Limited. All rights reserved.
 * The source code is the property of Malond Limited.
 ******************************************************************************/

import React, { useEffect } from "react"
import {
  BrowserRouter,
  HashRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom"
import { Provider as JotaiProvider, getDefaultStore } from "jotai"
import { SWRConfig, mutate } from "swr"
import { axiosFetcher } from "../libs/api/axiosFetcher"
import LoadingSale from "../components/Loading/LoadingSale"
import { setGlobalNavigate } from "../libs/compat/router"
import { KRATOS_WHOAMI_KEY } from "../hooks/useUser"
import { useAuth } from "../hooks/useAuth"

// Pages
import IndexPage from "../pages/index"
import LoginPage from "../pages/login"
import LogoutPage from "../pages/logout"
import WelcomePage from "../pages/welcome"
import PingPage from "../pages/ping"
import EnterpriseSelectPage from "../pages/e/index"
import EnterprisePage from "../pages/e/[mainEnterpriseId]/index"
import ShopSelectRedirectPage from "../pages/e/[mainEnterpriseId]/s/index"
import ShopPage from "../pages/e/[mainEnterpriseId]/s/[shopId]/index"
import HubPage from "../pages/e/[mainEnterpriseId]/s/[shopId]/[shopSalePointId]/index"
import SalesPage from "../pages/e/[mainEnterpriseId]/s/[shopId]/[shopSalePointId]/sales/index"
import OrdersPage from "../pages/e/[mainEnterpriseId]/s/[shopId]/[shopSalePointId]/orders/index"
import RevisionPage from "../pages/e/[mainEnterpriseId]/s/[shopId]/[shopSalePointId]/revision/index"

// Determine Router type (HashRouter for Electron static builds, BrowserRouter for Web)
const isElectron =
  typeof window !== "undefined" &&
  (Boolean((window as any).electron) ||
    Boolean((window as any).electronAuth) ||
    Boolean((window as any).process?.type))

const RouterComponent = isElectron ? HashRouter : BrowserRouter

const defaultStore = getDefaultStore()

// Helper component to bind global navigation for imperative Router.push calls and auth events
function NavigationBinder() {
  const navigate = useNavigate()
  useEffect(() => {
    setGlobalNavigate(navigate)

    const handleSessionExpired = () => {
      console.warn(
        "[App]: Session expired event received, redirecting to /login"
      )
      mutate(KRATOS_WHOAMI_KEY, null, false)
      navigate("/login")
    }

    window.addEventListener("auth:session-expired", handleSessionExpired)
    return () => {
      window.removeEventListener("auth:session-expired", handleSessionExpired)
    }
  }, [navigate])
  return null
}

// Global initializer to restore session and atoms upon cold start or page reload (Ctrl+R)
function AuthSessionInitializer() {
  const { checkAuth } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    let isMounted = true
    const currentPath = location.pathname

    checkAuth(false)
      .then((isAuth) => {
        if (!isMounted) return
        if (!isAuth) {
          const publicPaths = ["/login", "/logout", "/ping", "/"]
          const isPublic = publicPaths.includes(currentPath)
          if (!isPublic) {
            navigate("/login", { replace: true })
          }
        }
      })
      .catch((err) => {
        console.warn(
          "[AuthSessionInitializer]: Session verification error:",
          err
        )
        if (!isMounted) return
        const publicPaths = ["/login", "/logout", "/ping", "/"]
        const isPublic = publicPaths.includes(currentPath)
        if (!isPublic) {
          navigate("/login", { replace: true })
        }
      })

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}

export function App() {
  return (
    <SWRConfig
      value={{
        fetcher: axiosFetcher,
        onError: (err) => {
          console.error("SWR Error:", err)
        },
      }}
    >
      <JotaiProvider store={defaultStore}>
        <RouterComponent>
          <NavigationBinder />
          <AuthSessionInitializer />
          <Routes>
            {/* Base Routes */}
            <Route path="/" element={<IndexPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/logout" element={<LogoutPage />} />
            <Route path="/welcome" element={<WelcomePage />} />
            <Route path="/ping" element={<PingPage />} />

            {/* Enterprise Routes */}
            {/* 1. Выбор предприятия */}
            <Route path="/e" element={<EnterpriseSelectPage />} />

            {/* 2. Выбор магазина (внутри предприятия) */}
            <Route path="/e/:mainEnterpriseId" element={<EnterprisePage />} />

            {/* 3. Выбор точки продаж (кассы) */}
            <Route path="/e/:mainEnterpriseId/:shopId" element={<ShopPage />} />
            <Route
              path="/e/:mainEnterpriseId/s/:shopId"
              element={<ShopPage />}
            />
            <Route
              path="/e/:mainEnterpriseId/s"
              element={<ShopSelectRedirectPage />}
            />

            {/* 4. Главное меню кассы (кассовый узел / Hub) */}
            <Route
              path="/e/:mainEnterpriseId/:shopId/:shopSalePointId"
              element={<HubPage />}
            />
            <Route
              path="/e/:mainEnterpriseId/s/:shopId/:shopSalePointId"
              element={<HubPage />}
            />

            {/* 5. POS Operations (Продажи, Ордера, Ревизия) */}
            <Route
              path="/e/:mainEnterpriseId/:shopId/:shopSalePointId/sales"
              element={<SalesPage />}
            />
            <Route
              path="/e/:mainEnterpriseId/s/:shopId/:shopSalePointId/sales"
              element={<SalesPage />}
            />

            <Route
              path="/e/:mainEnterpriseId/:shopId/:shopSalePointId/orders"
              element={<OrdersPage />}
            />
            <Route
              path="/e/:mainEnterpriseId/s/:shopId/:shopSalePointId/orders"
              element={<OrdersPage />}
            />

            <Route
              path="/e/:mainEnterpriseId/:shopId/:shopSalePointId/revision"
              element={<RevisionPage />}
            />
            <Route
              path="/e/:mainEnterpriseId/s/:shopId/:shopSalePointId/revision"
              element={<RevisionPage />}
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          <LoadingSale />
        </RouterComponent>
      </JotaiProvider>
    </SWRConfig>
  )
}

export default App
